import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  CalendarBlank, Plus, Clock, Check, X, PencilSimple, Trash, EnvelopeSimple,
  Phone, ChatCircleDots, GoogleLogo, Sparkle, ArrowRight, List, GridFour,
  Sun, CalendarBlank as CalendarBlankIcon, MagnifyingGlass, CalendarStar,
  MapPin, Users, VideoCamera, Link as LinkIcon,
} from '@phosphor-icons/react';
import useScheduleStore from '../hooks/useScheduleStore';
import { ScheduleDayView, ScheduleWeekView, ScheduleMonthView, ScheduleYearView } from '../components/ScheduleGridViews';
import TimezoneSelect from '../components/TimezoneSelect';
import { detectBrowserTimezone, wallClockToUTC, getWallClock } from '../lib/tz';
import { downloadIcs } from '../lib/ics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1h 30 min' },
  { value: 120, label: '2 hours' },
];

const CHANNELS = [
  { id: 'email',           label: 'Email',           icon: EnvelopeSimple },
  { id: 'sms',             label: 'SMS',             icon: Phone },
  { id: 'whatsapp',        label: 'WhatsApp',        icon: ChatCircleDots },
  { id: 'google_calendar', label: 'Google Calendar', icon: GoogleLogo },
];

function toLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// Wall-clock components observed in `tz` for the given UTC instant, formatted
// for an <input type="datetime-local">.
function isoToWallClockInputValue(iso, tz) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const wc = getWallClock(d, tz);
  const pad = (n) => String(n).padStart(2, '0');
  return `${wc.year}-${pad(wc.month)}-${pad(wc.day)}T${pad(wc.hour)}:${pad(wc.minute)}`;
}
function fromLocalInputValue(v) {
  if (!v) return '';
  return new Date(v).toISOString();
}
// Interpret a "YYYY-MM-DDTHH:mm" input value as wall-clock in `tz` → UTC ISO.
function wallClockInputToIso(v, tz) {
  if (!v) return '';
  const [datePart, timePart] = v.split('T');
  if (!datePart || !timePart) return '';
  const [y, mo, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  return wallClockToUTC(y, mo, d, hh, mm, tz).toISOString();
}
function formatWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Build the deep link for a channel chip on an appointment card.
// Returns null if we don't have enough data to open something useful.
function channelHref(channel, item) {
  if (!item) return null;
  if (channel === 'email') {
    const to = (item.customer_email || '').trim();
    if (!to) return null;
    const subject = encodeURIComponent(item.title || 'Appointment');
    const lines = [
      `Hi ${item.customer_name || ''},`.trim(),
      '',
      `Following up on our appointment "${item.title || 'Appointment'}" on ${formatWhen(item.start_at)}.`,
      '',
    ].join('\n');
    return `mailto:${to}?subject=${subject}&body=${encodeURIComponent(lines)}`;
  }
  if (channel === 'whatsapp') {
    const raw = (item.customer_phone || '').trim();
    if (!raw) return null;
    // wa.me requires the number without the leading +
    const digits = raw.replace(/[^0-9]/g, '');
    if (!digits) return null;
    const text = encodeURIComponent(
      `Hi ${item.customer_name || ''}, reminder for "${item.title || 'Appointment'}" on ${formatWhen(item.start_at)}.`.trim()
    );
    return `https://wa.me/${digits}?text=${text}`;
  }
  if (channel === 'sms') {
    const raw = (item.customer_phone || '').trim();
    if (!raw) return null;
    const text = `Reminder for "${item.title || 'Appointment'}" on ${formatWhen(item.start_at)}.`;
    return `sms:${raw}?body=${encodeURIComponent(text)}`;
  }
  if (channel === 'google_calendar') {
    if (!item.start_at) return null;
    const start = new Date(item.start_at);
    if (isNaN(start.getTime())) return null;
    const durMin = Number(item.duration_minutes) || 30;
    const end = new Date(start.getTime() + durMin * 60 * 1000);
    const fmt = (d) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
    };
    const params = new URLSearchParams({
      action:   'TEMPLATE',
      text:     item.title || 'Appointment',
      dates:    `${fmt(start)}/${fmt(end)}`,
      details:  (item.notes || '') + (item.customer_name ? `\nWith: ${item.customer_name}` : ''),
      location: item.location || '',
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }
  return null;
}

const DEFAULT_FORM = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  title: 'Discovery call',
  start_at: '',
  duration_minutes: 30,
  notes: '',
  channels: ['email'],
  timezone: '',
  location: '',
  attendees: [],            // list of invitee emails
  attendees_input: '',      // raw text the user is currently typing
  meeting_provider: 'none', // 'none' | 'google_meet' | 'zoom' | 'teams' | 'custom'
  meeting_link: '',
};

const MEETING_PROVIDERS = [
  { id: 'none',        label: 'No video call',     generate: null },
  { id: 'google_meet', label: 'Google Meet',       generate: 'https://meet.new' },
  { id: 'zoom',        label: 'Zoom',              generate: 'https://zoom.us/start/videomeeting' },
  { id: 'teams',       label: 'Microsoft Teams',   generate: 'https://teams.microsoft.com/start' },
  { id: 'custom',      label: 'Custom link',       generate: null },
];

export default function SchedulePage() {
  const [view, setView] = useState('list'); // 'list' | 'day' | 'week' | 'month' | 'year'
  const [scope, setScope] = useState('upcoming');
  // Grid views need both past and future events visible.
  const effectiveScope = view === 'list' ? scope : 'all';
  const { items, loading, isGuest, create, update, remove } = useScheduleStore(effectiveScope);
  const [gridCursor, setGridCursor] = useState(() => new Date());
  const [gridTz, setGridTz] = useState(() => detectBrowserTimezone());

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [search, setSearch] = useState('');
  const [googleConnected, setGoogleConnected] = useState(false);
  const navigate = useNavigate();

  // Fetch Google Calendar connect status for signed-in users (used by the
  // "Sync this calendar with Google" prompt in Year view).
  useEffect(() => {
    if (isGuest) { setGoogleConnected(false); return; }
    let cancelled = false;
    axios.get(`${API}/oauth/calendar/status`, { withCredentials: true })
      .then((r) => { if (!cancelled) setGoogleConnected(Boolean(r.data?.connected)); })
      .catch(() => { /* not configured / unauthorised — treat as disconnected */ });
    return () => { cancelled = true; };
  }, [isGuest]);

  const handleConnectGoogle = () => {
    if (isGuest) {
      navigate('/register?next=/booking/settings');
      return;
    }
    navigate('/booking/settings');
  };

  const handleExportIcs = () => {
    if (!filteredItems.length) {
      toast.error('No appointments to export');
      return;
    }
    try {
      downloadIcs(filteredItems, 'bil-schedule.ics');
      toast.success(`Exported ${filteredItems.length} appointment${filteredItems.length === 1 ? '' : 's'} to .ics`);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  // Apply search filter across customer details, title, and notes.
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const haystack = [
        it.customer_name, it.customer_email, it.customer_phone,
        it.title, it.notes,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [items, search]);

  // Ask for browser notification permission once
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window
        && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch (_) { /* ignore */ }
    }
  }, []);

  const openCreate = (prefill) => {
    setEditing(null);
    if (prefill && prefill.year) {
      // Coming from a grid cell click: use the wall-clock components in the chosen tz
      const pad = (n) => String(n).padStart(2, '0');
      const start_at = `${prefill.year}-${pad(prefill.month)}-${pad(prefill.day)}T${pad(prefill.hour)}:${pad(prefill.minute)}`;
      setForm({ ...DEFAULT_FORM, start_at, timezone: prefill.tz || gridTz });
    } else {
      // Default to the grid's tz so the form's wall-clock aligns with the calendar render.
      const tz = gridTz || detectBrowserTimezone();
      const now = new Date();
      now.setHours(now.getHours() + 1, 0, 0, 0);
      setForm({ ...DEFAULT_FORM, start_at: isoToWallClockInputValue(now.toISOString(), tz), timezone: tz });
    }
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    // Always show the event in the grid's tz so the form's "When" matches the
    // exact slot the user is looking at in the calendar (no off-by-X-hours surprises).
    const tz = gridTz || item.timezone || detectBrowserTimezone();
    setForm({
      customer_name: item.customer_name || '',
      customer_email: item.customer_email || '',
      customer_phone: item.customer_phone || '',
      title: item.title || '',
      start_at: isoToWallClockInputValue(item.start_at, tz),
      duration_minutes: item.duration_minutes || 30,
      notes: item.notes || '',
      channels: Array.isArray(item.channels) ? item.channels : ['email'],
      timezone: tz,
      location: item.location || '',
      attendees: Array.isArray(item.attendees) ? item.attendees : [],
      attendees_input: '',
      meeting_provider: item.meeting_provider || 'none',
      meeting_link: item.meeting_link || '',
    });
    setOpen(true);
  };

  const toggleChannel = (id) => {
    setForm((f) => {
      const has = f.channels.includes(id);
      return { ...f, channels: has ? f.channels.filter((c) => c !== id) : [...f.channels, id] };
    });
  };

  // ---- Attendee chip helpers --------------------------------------------------
  const addAttendee = (raw) => {
    const email = (raw || '').trim().toLowerCase();
    if (!email) return;
    // Lightweight email shape check — server re-validates
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(`"${raw}" is not a valid email`);
      return;
    }
    setForm((f) => {
      if (f.attendees.includes(email)) return { ...f, attendees_input: '' };
      return { ...f, attendees: [...f.attendees, email], attendees_input: '' };
    });
  };
  const removeAttendee = (email) => {
    setForm((f) => ({ ...f, attendees: f.attendees.filter((e) => e !== email) }));
  };
  const onAttendeeKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === 'Tab') {
      if (form.attendees_input.trim()) {
        e.preventDefault();
        addAttendee(form.attendees_input);
      }
    } else if (e.key === 'Backspace' && !form.attendees_input && form.attendees.length) {
      removeAttendee(form.attendees[form.attendees.length - 1]);
    }
  };

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    if (!form.customer_email.trim()) { toast.error('Customer email is required'); return; }
    if (!form.start_at) { toast.error('Pick a date & time'); return; }
    const tz = form.timezone || gridTz || detectBrowserTimezone();
    const start_iso = wallClockInputToIso(form.start_at, tz);
    const payload = {
      title: (form.title || 'Appointment').trim(),
      start_at: start_iso,
      notes: form.notes.trim(),
      duration_minutes: Number(form.duration_minutes) || 30,
      customer_name: form.customer_name.trim(),
      customer_email: form.customer_email.trim(),
      customer_phone: form.customer_phone.trim(),
      channels: form.channels,
      timezone: tz,
      location: form.location.trim(),
      attendees: form.attendees,
      meeting_provider: form.meeting_provider === 'none' ? null : form.meeting_provider,
      meeting_link: form.meeting_link.trim(),
      remind_minutes_before: 60,
      color: '#002677',
    };
    try {
      if (editing) {
        await update(editing.id, { ...payload, reminded: false, reminded_24h: false, reminded_1h: false });
        toast.success('Appointment updated');
      } else {
        await create(payload);
        toast.success('Appointment booked');
      }
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const markDone = async (item) => {
    try { await update(item.id, { status: 'done' }); toast.success('Marked done'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const cancelEvent = async (item) => {
    try { await update(item.id, { status: 'cancelled' }); toast.success('Appointment cancelled'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const removeEvent = async (item) => {
    if (!window.confirm(`Delete appointment for ${item.customer_name || item.title}?`)) return;
    try { await remove(item.id); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const groups = useMemo(() => {
    const m = new Map();
    filteredItems.forEach((it) => {
      const d = new Date(it.start_at);
      const key = d.toLocaleDateString(undefined, {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
      });
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(it);
    });
    return Array.from(m.entries());
  }, [filteredItems]);

  const body = (
    <div className="space-y-6" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#002677]">
            <CalendarBlank size={20} weight="duotone" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]">Free tool</span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">
            Schedule &amp; Remind
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Book customer appointments. Reminders fire 24h and 1h before via the chosen channels.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass
              size={14}
              weight="bold"
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, title…"
              className="h-9 pl-8 pr-8 w-[240px] rounded-sm text-sm"
              data-testid="schedule-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                data-testid="schedule-search-clear"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
          <div className="inline-flex border border-border rounded-sm bg-background overflow-hidden">
            {[
              { id: 'list',  label: 'List',  icon: List },
              { id: 'day',   label: 'Day',   icon: Sun },
              { id: 'week',  label: 'Week',  icon: CalendarBlankIcon },
              { id: 'month', label: 'Month', icon: GridFour },
              { id: 'year',  label: 'Year',  icon: CalendarStar },
            ].map((v) => {
              const Icon = v.icon;
              const active = view === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setView(v.id)}
                  data-testid={`schedule-view-${v.id}`}
                  className={`h-9 px-3 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 transition-colors ${
                    active ? 'bg-[#002677] text-white' : 'text-muted-foreground hover:bg-accent/40'
                  }`}
                >
                  <Icon size={13} weight={active ? 'fill' : 'regular'} /> {v.label}
                </button>
              );
            })}
          </div>
          {view === 'list' && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-[170px] rounded-sm" data-testid="schedule-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="past">Past</SelectItem>
                <SelectItem value="all">All appointments</SelectItem>
              </SelectContent>
            </Select>
          )}
          {view !== 'list' && (
            <TimezoneSelect
              value={gridTz}
              onChange={setGridTz}
              testId="schedule-grid-tz"
              className="w-[240px]"
            />
          )}
          <Button
            onClick={openCreate}
            className="rounded-sm bg-[#002677] hover:bg-[#002677]/90"
            data-testid="schedule-new-btn"
          >
            <Plus size={16} weight="bold" className="mr-1.5" /> New appointment
          </Button>
        </div>
      </div>

      {/* Guest mode banner */}
      {isGuest && (
        <div
          className="rounded-sm border border-[#002677]/20 bg-[#002677]/5 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="schedule-guest-banner"
        >
          <Sparkle size={18} weight="fill" className="text-[#002677] flex-shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-semibold">Saved on this device.</span>
            <span className="text-muted-foreground"> Create a free account to sync across devices and send actual email/SMS reminders.</span>
          </div>
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#002677] text-white text-sm font-medium hover:bg-[#002677]/90"
            data-testid="schedule-guest-signup"
          >
            Create free account <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : view === 'day' ? (
        <ScheduleDayView
          cursor={gridCursor}
          setCursor={setGridCursor}
          items={filteredItems}
          onOpenItem={openEdit}
          onCreateAt={openCreate}
          onUpdate={(id, patch) => update(id, patch).catch((e) =>
            toast.error(e.response?.data?.detail || 'Update failed'))}
          tz={gridTz}
        />
      ) : view === 'week' ? (
        <ScheduleWeekView
          cursor={gridCursor}
          setCursor={setGridCursor}
          items={filteredItems}
          onOpenItem={openEdit}
          onCreateAt={openCreate}
          onUpdate={(id, patch) => update(id, patch).catch((e) =>
            toast.error(e.response?.data?.detail || 'Update failed'))}
          tz={gridTz}
        />
      ) : view === 'month' ? (
        <ScheduleMonthView
          cursor={gridCursor}
          setCursor={setGridCursor}
          items={filteredItems}
          onOpenItem={openEdit}
          onCreateAt={openCreate}
          tz={gridTz}
        />
      ) : view === 'year' ? (
        <ScheduleYearView
          cursor={gridCursor}
          setCursor={setGridCursor}
          items={filteredItems}
          tz={gridTz}
          onDrilldown={(date, nextView) => {
            setGridCursor(date);
            setView(nextView);
          }}
          syncPrompt={{
            isGuest,
            googleConnected,
            onConnect: handleConnectGoogle,
            onExport: handleExportIcs,
          }}
        />
      ) : filteredItems.length === 0 && search ? (
        <Card className="p-10 text-center rounded-sm border-dashed" data-testid="schedule-no-search-results">
          <MagnifyingGlass size={48} className="mx-auto text-muted-foreground" weight="duotone" />
          <h3 className="font-heading text-xl mt-4 font-semibold">No matches for &ldquo;{search}&rdquo;</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-5">
            Try a different name, email, phone, or keyword.
          </p>
          <Button
            variant="outline"
            onClick={() => setSearch('')}
            className="rounded-sm"
            data-testid="schedule-no-search-clear"
          >
            Clear search
          </Button>
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center rounded-sm border-dashed">
          <CalendarBlank size={48} className="mx-auto text-muted-foreground" weight="duotone" />
          <h3 className="font-heading text-xl mt-4 font-semibold">
            No {scope === 'past' ? 'past' : 'upcoming'} appointments
          </h3>
          <p className="text-sm text-muted-foreground mt-1 mb-5">
            Book a customer appointment and we&apos;ll remind you automatically.
          </p>
          <Button
            onClick={openCreate}
            className="rounded-sm bg-[#002677] hover:bg-[#002677]/90"
            data-testid="schedule-empty-cta"
          >
            <Plus size={16} weight="bold" className="mr-1.5" /> Book your first appointment
          </Button>
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map(([day, evts]) => (
            <div key={day}>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">{day}</div>
              <div className="space-y-2">
                {evts.map((item) => (
                  <Card
                    key={item.id}
                    className="rounded-sm border-l-4 p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:shadow-sm transition-shadow"
                    style={{ borderLeftColor: item.color || '#002677' }}
                    data-testid={`schedule-item-${item.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-sm truncate">
                          {item.customer_name || 'Guest'}
                          <span className="text-muted-foreground font-normal"> · {item.title}</span>
                        </h4>
                        {item.status === 'done' && <Badge variant="secondary" className="rounded-sm h-5">Done</Badge>}
                        {item.status === 'cancelled' && <Badge variant="outline" className="rounded-sm h-5">Cancelled</Badge>}
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Clock size={12} /> {formatWhen(item.start_at)} · {item.duration_minutes || 30} min
                        </span>
                        {item.customer_email && (
                          <span className="inline-flex items-center gap-1"><EnvelopeSimple size={12} /> {item.customer_email}</span>
                        )}
                        {item.customer_phone && (
                          <span className="inline-flex items-center gap-1"><Phone size={12} /> {item.customer_phone}</span>
                        )}
                        {item.location && (
                          <span className="inline-flex items-center gap-1" data-testid={`schedule-item-location-${item.id}`}>
                            <MapPin size={12} /> {item.location}
                          </span>
                        )}
                        {Array.isArray(item.attendees) && item.attendees.length > 0 && (
                          <span className="inline-flex items-center gap-1" title={item.attendees.join(', ')} data-testid={`schedule-item-attendees-${item.id}`}>
                            <Users size={12} /> {item.attendees.length} invited
                          </span>
                        )}
                        {item.meeting_link && (
                          <a
                            href={item.meeting_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-[#002677] hover:underline font-semibold"
                            data-testid={`schedule-item-meeting-link-${item.id}`}
                          >
                            <VideoCamera size={12} /> Join {item.meeting_provider === 'google_meet' ? 'Meet' : item.meeting_provider === 'zoom' ? 'Zoom' : item.meeting_provider === 'teams' ? 'Teams' : 'call'}
                          </a>
                        )}
                      </div>
                      {item.channels && item.channels.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {item.channels.map((c) => {
                            const meta = CHANNELS.find((x) => x.id === c);
                            if (!meta) return null;
                            const href = channelHref(c, item);
                            const Icon = meta.icon;
                            const className = `inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-[#002677]/10 text-[#002677] ${href ? 'hover:bg-[#002677]/20 transition-colors cursor-pointer' : 'opacity-70'}`;
                            const content = (<><Icon size={11} weight="bold" /> {meta.label}</>);
                            if (!href) {
                              return <span key={c} className={className}>{content}</span>;
                            }
                            return (
                              <a
                                key={c}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className={className}
                                data-testid={`schedule-channel-link-${c}-${item.id}`}
                                title={`Open ${meta.label}`}
                              >
                                {content}
                              </a>
                            );
                          })}
                        </div>
                      )}
                      {item.notes && (
                        <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{item.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {item.status !== 'done' && (
                        <Button size="sm" variant="ghost" className="h-8 rounded-sm"
                          onClick={() => markDone(item)} data-testid={`schedule-done-${item.id}`} title="Mark done">
                          <Check size={14} />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-8 rounded-sm"
                        onClick={() => openEdit(item)} data-testid={`schedule-edit-${item.id}`} title="Edit">
                        <PencilSimple size={14} />
                      </Button>
                      {item.status !== 'cancelled' && item.status !== 'done' && (
                        <Button size="sm" variant="ghost" className="h-8 rounded-sm"
                          onClick={() => cancelEvent(item)} data-testid={`schedule-cancel-${item.id}`} title="Cancel appointment">
                          <X size={14} />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-8 rounded-sm text-destructive hover:text-destructive"
                        onClick={() => removeEvent(item)} data-testid={`schedule-delete-${item.id}`} title="Delete">
                        <Trash size={14} />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New appointment dialog (DIL-style) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editing ? 'Edit appointment' : 'New appointment'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                  Customer name <span className="text-red-500">*</span>
                </Label>
                <Input
                  data-testid="schedule-form-customer-name"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  data-testid="schedule-form-customer-email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                Phone (E.164, e.g. +14155552671)
              </Label>
              <Input
                data-testid="schedule-form-customer-phone"
                value={form.customer_phone}
                placeholder="+4915756342764"
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                className="rounded-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Title</Label>
              <Input
                data-testid="schedule-form-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Discovery call with BIL"
                className="rounded-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                  When <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="datetime-local"
                  data-testid="schedule-form-when"
                  value={form.start_at}
                  onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Duration</Label>
                <Select
                  value={String(form.duration_minutes)}
                  onValueChange={(v) => setForm({ ...form, duration_minutes: Number(v) })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="schedule-form-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map((d) => (
                      <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                Timezone
              </Label>
              <TimezoneSelect
                value={form.timezone || gridTz || detectBrowserTimezone()}
                onChange={(newTz) => {
                  setForm((f) => {
                    // Preserve the same UTC instant: re-render the "When" input
                    // as wall-clock in the newly chosen timezone so it stays in
                    // sync with what the calendar will display.
                    const currentTz = f.timezone || gridTz || detectBrowserTimezone();
                    if (!f.start_at) return { ...f, timezone: newTz };
                    const iso = wallClockInputToIso(f.start_at, currentTz);
                    const reformatted = isoToWallClockInputValue(iso, newTz);
                    return { ...f, timezone: newTz, start_at: reformatted };
                  });
                }}
                testId="schedule-form-tz"
                className="w-full"
              />
              <p className="text-[11px] text-muted-foreground">
                Times above are wall-clock in this timezone — matches what the calendar shows. Change the zone to convert (the time field updates automatically).
              </p>
            </div>

            {/* Where — physical address or venue */}
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold inline-flex items-center gap-1.5">
                <MapPin size={11} weight="bold" /> Where
              </Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Add room, address, or venue"
                className="rounded-sm"
                data-testid="schedule-form-location"
              />
            </div>

            {/* Invitees — chip input */}
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold inline-flex items-center gap-1.5">
                <Users size={11} weight="bold" /> Invite others
              </Label>
              <div className="flex flex-wrap items-center gap-1.5 min-h-9 px-2 py-1.5 border border-border rounded-sm bg-background">
                {form.attendees.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#002677]/10 text-[#002677] text-xs font-medium"
                    data-testid={`schedule-form-attendee-${email}`}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeAttendee(email)}
                      className="hover:text-[#002677]/70"
                      aria-label={`Remove ${email}`}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                ))}
                <input
                  type="email"
                  value={form.attendees_input}
                  onChange={(e) => setForm({ ...form, attendees_input: e.target.value })}
                  onKeyDown={onAttendeeKeyDown}
                  onBlur={() => form.attendees_input.trim() && addAttendee(form.attendees_input)}
                  placeholder={form.attendees.length ? 'Add another…' : 'name@example.com'}
                  className="flex-1 min-w-[160px] outline-none bg-transparent text-sm placeholder:text-muted-foreground"
                  data-testid="schedule-form-attendees-input"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Press Enter, comma, or space to add. They will receive the confirmation email and reminders if the email channel is enabled.
              </p>
            </div>

            {/* Meeting link / video call */}
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold inline-flex items-center gap-1.5">
                <VideoCamera size={11} weight="bold" /> Add video conferencing
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Select
                  value={form.meeting_provider}
                  onValueChange={(v) => setForm({ ...form, meeting_provider: v, meeting_link: v === 'none' ? '' : form.meeting_link })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="schedule-form-meeting-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEETING_PROVIDERS.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.meeting_provider !== 'none' && (
                  <div className="relative">
                    <LinkIcon
                      size={12} weight="bold"
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                    />
                    <Input
                      type="url"
                      value={form.meeting_link}
                      onChange={(e) => setForm({ ...form, meeting_link: e.target.value })}
                      placeholder="https://meet.google.com/abc-defg-hij"
                      className="rounded-sm pl-7"
                      data-testid="schedule-form-meeting-link"
                    />
                  </div>
                )}
              </div>
              {form.meeting_provider !== 'none' && (() => {
                const provider = MEETING_PROVIDERS.find((p) => p.id === form.meeting_provider);
                if (!provider || !provider.generate) return null;
                return (
                  <p className="text-[11px] text-muted-foreground">
                    Don&apos;t have a link?{' '}
                    <a
                      href={provider.generate}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#002677] font-medium hover:underline"
                      data-testid="schedule-form-meeting-generate"
                    >
                      Open {provider.label} → start a meeting
                    </a>
                    {' '}then paste the URL above.
                  </p>
                );
              })()}
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                Notes (optional)
              </Label>
              <Textarea
                rows={3}
                data-testid="schedule-form-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                Reminder channels
              </Label>
              <div className="flex flex-wrap gap-2">
                {CHANNELS.map((c) => {
                  const Icon = c.icon;
                  const active = form.channels.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleChannel(c.id)}
                      data-testid={`schedule-form-channel-${c.id}`}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
                        active
                          ? 'bg-[#002677] text-white border-[#002677]'
                          : 'bg-background text-foreground border-border hover:border-[#002677]/40'
                      }`}
                    >
                      <Icon size={14} weight={active ? 'fill' : 'regular'} /> {c.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Confirmation fires now. Reminders fire 24h and 1h before via the chosen channels.
                <span className="block mt-1 text-[11px]">
                  <strong>Email:</strong> sending from a verified address (currently the Resend test
                  domain — recipients receive normally once you verify <code>ubil.net</code> in Resend).{' '}
                  <strong>SMS:</strong> needs a purchased Twilio number (not yet set).{' '}
                  <strong>WhatsApp:</strong> sandbox mode — recipients must first text
                  <code> join &lt;your-code&gt; </code>
                  to <code>+1 415 523 8886</code>. The in-browser reminder always fires.
                </span>
              </p>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={submit}
              className="rounded-sm bg-[#002677] hover:bg-[#002677]/90 text-white"
              data-testid="schedule-form-submit"
            >
              {editing ? 'Save changes' : 'Book appointment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Guests: wrap in marketing-style shell so it works without the app sidebar.
  if (isGuest) {
    return (
      <div className="min-h-screen bg-background" data-testid="schedule-public">
        <header className="w-full bg-[#f5f6fa] dark:bg-muted/30 border-b border-border">
          <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-3">
            <Link to="/login" className="flex items-center gap-3">
              <div
                className="h-12 w-14 flex items-center justify-center text-white font-heading font-bold text-lg tracking-tight"
                style={{ background: '#002677' }}
              >
                BIL
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold leading-tight">Schedule &amp; Remind</p>
                <p className="text-xs text-muted-foreground leading-tight">Free tool · No signup required</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
                data-testid="schedule-public-signin"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="h-9 px-4 inline-flex items-center text-sm font-medium rounded-sm bg-[#002677] text-white hover:bg-[#002677]/90"
                data-testid="schedule-public-register"
              >
                Create free account
              </Link>
            </div>
          </div>
        </header>
        <main className="max-w-[1200px] mx-auto px-6 py-8">{body}</main>
      </div>
    );
  }

  // Signed-in users keep the standard app layout (provided by the router).
  return body;
}
